import React, { createContext, useContext, useState } from 'react';
import { ChevronRight, MessageSquarePlus } from 'lucide-react';
import { useFieldCommentsContext, FieldCommentNote } from './fieldComments';
import { fieldCommentAddBtn, fieldCommentAddBtnActive, inputXs, detailLabel, detailValue, detailGridGap, sectionTitle, sectionTitleBar } from './constants';

// One detail-page section card (docs/workflow-ui.md §4.3): white card, small
// bold title, optional count / right-side actions. `collapsible`
// sections render a chevron toggle (History-style) — secondary info collapses
// by default per the 詳細但分區 house rule.
export const SectionCard = ({
  title,
  count = null,
  actions = null,
  collapsible = false,
  defaultOpen = true,
  children,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const heading = (
    <>
      <h2 className={sectionTitle}>
        {!collapsible && <span className={sectionTitleBar} aria-hidden="true" />}
        {title}
      </h2>
      {count !== null && <span className="text-sm text-slate-500">{count}</span>}
    </>
  );

  return (
    <div className={`bg-white rounded-lg border border-slate-200 p-5 mb-4 ${className}`}>
      {collapsible ? (
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left">
            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} />
            {heading}
          </button>
          {actions}
        </div>
      ) : (
        <div className="flex items-center gap-2 mb-3">
          {heading}
          {actions && <span className="ml-auto">{actions}</span>}
        </div>
      )}
      {(!collapsible || open) && <div className={collapsible ? 'mt-3' : ''}>{children}</div>}
    </div>
  );
};

// Label/value reading layout for detail sections — replaces the six local
// `Detail` copies. Usage: <DetailGrid><DetailItem label="UEN">…</DetailItem></DetailGrid>
export const DetailGrid = ({ cols = 2, children }) => (
  <div className={`grid grid-cols-1 ${cols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} ${detailGridGap}`}>
    {children}
  </div>
);

// ── "changed since it was returned" plumbing ────────────────────────────────
// A detail page wraps its content in <ReturnDiffProvider> with the map from
// changeDiff.changesSinceReturn(); any DetailItem carrying a matching `field`
// then shows the returned-version value struck through in front of the current
// one. Nothing is wired = nothing changes: pages without a
// provider, and fields the requester did not touch, render exactly as before.
//
// This is the FINE treatment. ChangesSinceReturnCard is the coarse one and the
// completeness guarantee — it lists every changed field whether or not a
// DetailItem claimed it, so wiring a `field` here can improve the reading but
// can never be the only thing standing between a reviewer and a change.
const ReturnDiffContext = createContext(null);

export const ReturnDiffProvider = ({ diff, fmt = null, children }) => (
  <ReturnDiffContext.Provider value={diff ? { diff, fmt } : null}>
    {children}
  </ReturnDiffContext.Provider>
);

// The struck-through previous value in front of the current one.
export const PreviousValue = ({ children }) => (
  <span className="text-slate-400 line-through decoration-slate-400 mr-1.5 break-words">
    {children}
  </span>
);

// ── 逐項意見 ────────────────────────────────────────────────────────────────
// 一格資料底下可能出現兩種便條，來源完全不同：
//   FieldCommentNote  — 上一次退件留下的意見，唯讀，只在單子停在 awaiting_info
//                       時出現（跟退回原因紅框同進退；元件在 fieldComments.js，
//                       因為 Modify modal 的欄位也要用同一個長相）
//   就地展開的 textarea — 審核者現在正在打的草稿，按下 Return for Changes 才送出
// 兩者都要 `field`：沒有 field 的 DetailItem 就只是一格資料，不長出任何入口。
export const DetailItem = ({ label, field = null, was = undefined, children }) => {
  const ctx = useContext(ReturnDiffContext);
  const fc = useFieldCommentsContext();
  const entry = field && ctx && ctx.diff[field] ? ctx.diff[field] : null;
  // `was` wins when given explicitly (a page that formats the value itself);
  // otherwise take the returned-version value from the provider.
  const previous = was !== undefined
    ? was
    : (entry ? (ctx.fmt ? ctx.fmt(field, entry.before) : entry.before) : undefined);
  const changed = previous !== undefined && previous !== null && previous !== '';
  const comment = field && fc ? fc.existingByField[field] : null;
  const canComment = Boolean(field && fc && fc.canComment);
  const draft = canComment ? fc.drafts[field] : undefined;
  const [editing, setEditing] = useState(draft !== undefined);

  return (
    <div className="group">
      <div className="flex items-start gap-2">
        <p className={detailLabel}>{label}</p>
        {canComment && !editing && (
          <button
            type="button"
            onClick={() => { setEditing(true); fc.set(field, '', label); }}
            className={`${fieldCommentAddBtn} ml-auto inline-flex items-center gap-0.5 shrink-0`}
            title={`Comment on ${label}`}
          >
            <MessageSquarePlus className="w-3 h-3" /> Comment
          </button>
        )}
      </div>
      {/* div, not p: callers pass block content (e.g. the covered-companies <ul>),
          which React rejects inside a <p> and the browser then unnests. */}
      <div className={detailValue}>
        {changed && <PreviousValue>{previous}</PreviousValue>}
        {children === null || children === undefined || children === '' ? '—' : children}
      </div>
      {comment && <FieldCommentNote entry={comment} />}
      {canComment && editing && (
        <div className="mt-1.5">
          <textarea
            rows="2"
            autoFocus
            value={draft || ''}
            onChange={(e) => fc.set(field, e.target.value, label)}
            placeholder="What needs to change here?"
            className={inputXs}
          />
          <button
            type="button"
            onClick={() => { fc.remove(field); setEditing(false); }}
            className={`${fieldCommentAddBtnActive} mt-0.5`}
          >
            Remove Comment
          </button>
        </div>
      )}
    </div>
  );
};

export default SectionCard;
